import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import {
  Search,
  Download,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Package,
  X
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import { api } from '@/shared/api/endpoints';
import type { IUserFinancialResponse, IUserPackage } from '@/shared/types';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";


export default function PackagesInvested() {
  const { user: authStoreUser, isAuthenticated } = useAuthStore();
  const [financialData, setFinancialData] = useState<IUserFinancialResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'roi'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selectedClosing, setSelectedClosing] = useState<IUserPackage | null>(null);


  const navigate = useNavigate();

  useEffect(() => {
    if (authStoreUser) {
      fetchFinancialData();
    }
  }, [authStoreUser]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const response = await api.user.getFinancialInfo();
      if (response.success && response.data) {
        setFinancialData(response.data);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedPackages = useMemo(() => {
    if (!financialData?.packages) return [];

    let filtered = financialData.packages.filter((pkg: IUserPackage) => {
      const matchesSearch = pkg._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.packageId.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.amount.toString().includes(searchTerm);

      const matchesStatus = statusFilter === 'all' || pkg.status === statusFilter;
      const matchesPaymentMethod = paymentMethodFilter === 'all' || pkg.paymentMethod === paymentMethodFilter;

      return matchesSearch && matchesStatus && matchesPaymentMethod;
    });

    // Sort packages
    filtered.sort((a: IUserPackage, b: IUserPackage) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'roi':
          comparison = a.monthlyROI - b.monthlyROI;
          break;
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [financialData?.packages, searchTerm, statusFilter, paymentMethodFilter, sortBy, sortOrder]);

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
      closed: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    };

    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
  };

  const getPaymentMethodBadge = (method: string) => {
    const badges = {
      cash: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
      coin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    };

    return badges[method as keyof typeof badges] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const calculateProgress = (pkg: IUserPackage) => {
    if (!pkg.nextWithdrawalDate || pkg.status != "active") return 0;

    let start = new Date(pkg.startDate).getTime();
    let end = new Date(pkg.nextWithdrawalDate).getTime();

    if (pkg.withdrawalCount > 0) {
      start = new Date(pkg.updatedAt).getTime();
      end = new Date(pkg.nextWithdrawalDate).getTime();
    }

    const now = Date.now();
    const totalDuration = end - start;
    const elapsed = Math.max(0, now - start);

    if (totalDuration <= 0) return 100;

    return Math.min(100, (elapsed / totalDuration) * 100);
  };


  const exportPackages = () => {
    const csvContent = [
      ['ID', 'Package Name', 'Amount', 'Monthly ROI', 'Status', 'Payment Method', 'Start Date', 'End Date'],
      ...filteredAndSortedPackages.map((pkg: IUserPackage) => [
        pkg._id,
        pkg.packageId.name,
        pkg.amount.toString(),
        pkg.monthlyROI.toString(),
        pkg.status,
        pkg.paymentMethod,
        new Date(pkg.startDate).toLocaleDateString(),
        pkg.endDate ? new Date(pkg.endDate).toLocaleDateString() : 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packages_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const closeSaving = async (closeRequest: IUserPackage) => {
    setClosing(true);
    try {

      const params = {
        amount: closeRequest.amount,
        packageId: closeRequest._id,
        type: "investment_closure",
        userId: closeRequest.userId,
        pkId: closeRequest.packageId._id
      }


      const response = await api.transaction.closePackage(params);

      if (response.success) {
        toast.success("Package closing request submitted");
        setClosing(false);

      }
    } catch (error) {
      console.error('Error processing closing:', error);
      toast.error("Closing request failed");
      setClosing(false);
    }
    finally {
      setShowWithdrawalModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header variant="dashboard" />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading packages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate('/login')
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header variant="dashboard" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Savings Packages</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              View and manage your savings packages
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchFinancialData}
              className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={exportPackages}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Packages</h3>
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">
              {filteredAndSortedPackages.filter((pkg: IUserPackage) => pkg.status === 'active').length}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total saved</h3>
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-600">
              {formatCurrency(filteredAndSortedPackages.filter((pkg: IUserPackage) => pkg.status === "active").
                reduce((sum: number, pkg: IUserPackage) => sum + pkg.amount, 0))}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Monthly ROI</h3>
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-600">
              {formatCurrency(
                filteredAndSortedPackages
                  .filter((pkg: IUserPackage) => pkg.status === "active")
                  .reduce((sum: number, pkg: IUserPackage) => sum + pkg.monthlyROI, 0)
              )}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Payment Methods</option>
                <option value="cash">Cash</option>
                <option value="coin">Coin</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as 'date' | 'amount' | 'roi');
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="date-desc">Latest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="amount-desc">Highest Amount</option>
                <option value="amount-asc">Lowest Amount</option>
                <option value="roi-desc">Highest ROI</option>
                <option value="roi-asc">Lowest ROI</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
            <span>Showing {filteredAndSortedPackages.length} packages</span>
          </div>
        </div>

        {/* Packages Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Package</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Monthly ROI</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Payment Method</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Nex WDRL Progress</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Dates</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-900 dark:text-white">Close</th>

                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {filteredAndSortedPackages.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <Package className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No packages found</h3>
                        <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedPackages.map((pkg: IUserPackage) => (
                    <tr key={pkg._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <Package className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {pkg.packageId.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {pkg.packageId.roiPercentage}% ROI • {pkg.packageId.durationInMonths} months
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-lg text-gray-900 dark:text-white">
                          {formatCurrency(pkg.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-lg text-green-600">
                          {formatCurrency(pkg.monthlyROI)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(pkg.status)}`}>
                          {pkg.status.charAt(0).toUpperCase() + pkg.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(pkg.paymentMethod)}`}>
                          {pkg.paymentMethod.charAt(0).toUpperCase() + pkg.paymentMethod.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${calculateProgress(pkg)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {Math.round(calculateProgress(pkg))}% complete
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-900 dark:text-white">
                            Start Date: {new Date(pkg.startDate).toLocaleDateString()}
                          </div>

                          {pkg.nextWithdrawalDate && pkg.status == "active" && (
                            <div className="text-blue-600 dark:text-blue-400">
                              Next Withdrawal:{" "}
                              {new Date(
                                pkg.nextWithdrawalDate
                              ).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {pkg.status == "active" && (
                          <button
                            onClick={() => {
                              setSelectedClosing(pkg);
                              setShowWithdrawalModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-700 p-1 rounded transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showWithdrawalModal && selectedClosing && (

          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full relative">
              {/* Close (X) button */}
              <button
                onClick={() => setSelectedClosing(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>

              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                  Close {selectedClosing.packageId.name} Plan
                </h3>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  Click <span className="font-medium text-gray-900">Confirm</span> to
                  submit a request for the system to close your{" "}
                  <span className="font-medium text-gray-900">
                    {selectedClosing.packageId.name}
                  </span>{" "}
                  active saving plan. Your request will be processed within 24–48 hours.
                </p>
              </div>

              <div className="flex space-x-4 p-6 border-t border-gray-200">
                {/* Cancel button */}
                <button
                  onClick={() => setSelectedClosing(null)}
                  className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>

                {/* Confirm button */}
                <button
                  onClick={() => closeSaving(selectedClosing)}
                  disabled={closing}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {closing ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>


        )}
      </main>
    </div>
  );
}
